<template>
  <div
    ref="scrollContainerRef"
    class="in-out-cards-widget-view"
    :style="style"
  >
    <div
      class="widget-wrapper"
      :style="{ marginLeft: widget.marginLeft + 'px' }"
    >
      <div
        class="horizontal-scroll-container"
        :class="{ 'no-data': !hasRealData }"
        :style="{ gap: widget.gap + 'px' }"
      >
        <div
          v-for="(month, index) in displayData"
          :key="index"
          class="card"
          :style="{ boxShadow: widget.boxShadow }"
        >
          <div class="month-header">
            <div
              class="month-name"
              :style="{ color: widget.textColor }"
            >
              {{ month.monthName }}
            </div>
            <div :class="['month-balance', month.netAmount >= 0 ? 'positive' : 'negative']">
              {{ month.netAmount >= 0 ? '£' + numberToHumanReadable(month.netAmount) : '-' + numberToHumanReadable(Math.abs(month.netAmount)) }}
            </div>
          </div>

          <div class="bars-container">
            <div class="bar-container">
              <div
                class="bar income"
                :style="{ height: month.inBarHeight + 'px', backgroundColor: widget.inColor }"
              />
              <div class="amount-container">
                <span
                  class="amount-label positive"
                  :style="{ color: widget.inColor }"
                >In</span>
                <span
                  class="amount-value"
                  :style="{ color: widget.textColor }"
                >£{{ numberToHumanReadable(month.in) }}</span>
              </div>
            </div>
            <div class="bar-container">
              <div
                class="bar expense"
                :style="{ height: month.outBarHeight + 'px', backgroundColor: widget.outColor }"
              />
              <div class="amount-container">
                <span
                  class="amount-label negative"
                  :style="{ color: widget.outColor }"
                >Out</span>
                <span
                  class="amount-value"
                  :style="{ color: widget.textColor }"
                >£{{ numberToHumanReadable(month.out) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay box when no real data -->
    <div
      v-if="!hasRealData"
      class="no-data-overlay"
    >
      <div class="overlay-content">
        <div class="overlay-text">
          We don't have enough data to display your past months balances.
          <span
            class="connect-link"
            @click="handleConnectAccountsClick"
          >connect an active account</span>
          to get the full picture
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InOutCardsWidgetView',
  props: {
    widget: {
      type: Object,
    },
  },
  data() {
    return {
      isScrolling: false,
      startX: 0,
      scrollLeft: 0,
      MAX_BAR_HEIGHT: 200,
    };
  },
  computed: {
    style() {
      return {
        top: `${this.widget.top}px`,
        zIndex: this.widget.zIndex,
        fontFamily: this.widget.fontFamily,
      };
    },
    hasRealData() {
      const data = this.widget.variableData;
      return data && Array.isArray(data) && data.length > 0;
    },
    displayData() {
      if (this.hasRealData) {
        return this.calculateBarHeights(this.widget.variableData);
      }
      // Return default placeholder data when no real data
      const placeholderData = [
        { monthName: 'Connect account', in: 0, out: 0 },
        { monthName: 'to see your data', in: 0, out: 0 },
      ];
      return this.calculateBarHeights(placeholderData);
    },
    variableData() {
      // Keep this for backward compatibility if needed elsewhere
      return this.displayData;
    },
  },
  mounted() {
    this.setupDragScroll();
  },
  methods: {
    calculateBarHeights(data) {
      if (!data || data.length === 0) { return []; }

      const allValues = data.flatMap(item => [item.in, item.out]);
      const maxValue = Math.max(...allValues);

      return data.map(item => {
        const inBarHeight = maxValue > 0 ? (item.in / maxValue) * this.MAX_BAR_HEIGHT : 20;
        const outBarHeight = maxValue > 0 ? (item.out / maxValue) * this.MAX_BAR_HEIGHT : 20;
        // Calculate netAmount dynamically
        const netAmount = item.in - item.out;

        return {
          ...item,
          inBarHeight,
          outBarHeight,
          netAmount, // Add the calculated netAmount
        };
      });
    },
    handleConnectAccountsClick() {
      this.$emit('userInteraction', {
        eventName: 'connectAccountsClicked',
      });
    },
    setupDragScroll() {
      const scrollContainer = this.$refs.scrollContainerRef;
      if (!scrollContainer) { return; }

      scrollContainer.addEventListener('mousedown', e => {
        // Don't allow scrolling when there's no real data
        if (!this.hasRealData) { return; }

        this.isScrolling = true;
        this.startX = e.pageX - scrollContainer.offsetLeft;
        this.scrollLeft = scrollContainer.scrollLeft;
        scrollContainer.style.cursor = 'grabbing';
      });

      scrollContainer.addEventListener('mouseleave', () => {
        this.isScrolling = false;
        if (this.hasRealData) {
          scrollContainer.style.cursor = 'grab';
        } else {
          scrollContainer.style.cursor = 'default';
        }
      });

      scrollContainer.addEventListener('mouseup', () => {
        this.isScrolling = false;
        if (this.hasRealData) {
          scrollContainer.style.cursor = 'grab';
        } else {
          scrollContainer.style.cursor = 'default';
        }
      });

      scrollContainer.addEventListener('mousemove', e => {
        if (!this.isScrolling || !this.hasRealData) { return; }
        e.preventDefault();
        const x = e.pageX - scrollContainer.offsetLeft;
        const walk = (x - this.startX) * 2; // Scroll speed multiplier
        scrollContainer.scrollLeft = this.scrollLeft - walk;
      });
    },
    numberToHumanReadable(num) {
      if (typeof num !== 'number') { return num; }

      return num.toLocaleString('en-GB', {
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.in-out-cards-widget-view {
  position: absolute;
  display: flex;
  width: 430px;
  left: 0;
  overflow-x: auto;
  padding: 10px 0;
  -webkit-overflow-scrolling: touch; /* For smooth scrolling on iOS */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome/Safari */
  }
  white-space: nowrap;
  cursor: grab; /* Show grab cursor to indicate draggable */

  .widget-wrapper {
    width: 100%;
    margin-right: 30px;
  }
}

.horizontal-scroll-container {
  display: flex;
  padding: 5px;
  width: 100%;

  &.no-data {
    opacity: 0.4; /* Fade the cards when no real data */
    pointer-events: none; /* Disable all interactions */
  }
}

.card {
  flex: 0 0 250px;
  min-width: 250px;
  max-width: 250px;
  background-color: white;
  border-radius: 25px;
  padding: 20px;
  height: 330px;
  display: flex;
  flex-direction: column;
}

.month-header {
  display: flex;
  justify-content: space-between;
  flex: 1;
}

.month-name {
  font-size: 20px;
  font-weight: 600;
}

.month-balance {
  font-size: 20px;
  font-weight: 600;
}

.positive {
  color: #13C636;
}

.negative {
  color: #FF67A1;
}

.bars-container {
  display: flex;
  flex: 1;
  justify-content: space-around;
  align-items: flex-end;
  height: 250px;
  margin-bottom: 5px;
}

.bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 87px;
}

.bar {
  width: 87px;
  border-radius: 10px 10px 0 0;
}

.amount-container {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.amount-label {
  font-size: 15px;
  margin-bottom: 5px;
}

.amount-value {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.no-data-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none; /* Allow clicks to pass through */

  .connect-link {
    pointer-events: auto; /* Enable clicks on the connect link */
  }
}

.overlay-content {
  background: linear-gradient(135deg, #F5D547 0%, #FFCE00 100%);
  border-radius: 20px;
  padding: 20px 25px;
  width: 360px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.overlay-text {
  color: #1a365d;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.connect-link {
  color: #2563eb;
  text-decoration: underline;
  font-weight: 700;
  cursor: pointer;
}
</style>
